"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSecondaryThemeColor = exports.getIsAAACompliant = exports.getIsAACompliant = exports.getColorContrastRatio = exports.hexToHSL = exports.hexToRGB = void 0;
var react_1 = require("@emotion/react");
var react_2 = require("react");
var theme_1 = require("../theme");
/**
 * Convert hex color to an array of RGB values
 */
var hexToRGB = function (hexString) {
    var r = "";
    var g = "";
    var b = "";
    if (hexString.length == 4) {
        // 3 digits
        r = "0x" + hexString[1] + hexString[1];
        g = "0x" + hexString[2] + hexString[2];
        b = "0x" + hexString[3] + hexString[3];
    }
    else if (hexString.length == 7) {
        // 6 digits
        r = "0x" + hexString[1] + hexString[2];
        g = "0x" + hexString[3] + hexString[4];
        b = "0x" + hexString[5] + hexString[6];
    }
    return [+r, +g, +b];
};
exports.hexToRGB = hexToRGB;
/**
 * Convert hex color to an array of HSL values
 */
var hexToHSL = function (hexString) {
    var _a = (0, exports.hexToRGB)(hexString), r = _a[0], g = _a[1], b = _a[2];
    r /= 255;
    g /= 255;
    b /= 255;
    var l = Math.max(r, g, b);
    var s = l - Math.min(r, g, b);
    var h = s
        ? l === r
            ? (g - b) / s
            : l === g
                ? 2 + (b - r) / s
                : 4 + (r - g) / s
        : 0;
    return [
        60 * h < 0 ? 60 * h + 360 : 60 * h,
        100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
        (100 * (2 * l - s)) / 2,
    ];
};
exports.hexToHSL = hexToHSL;
/**
 * Helper method for calculating relative luminance
 * Formula: If value <= 0.03928 Then value/12.92 Else ((value+0.055)/1.055) ^ 2.4
 * Source: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
var calculateRelativeLuminanceComponent = function (rgbValue) {
    var relativeRgb = rgbValue / 255;
    return relativeRgb <= 0.03928
        ? relativeRgb / 12.92
        : Math.pow(((relativeRgb + 0.055) / 1.055), 2.4);
};
/**
 * Uses WCAG 2.x formula to calculate Relative Luminance for a color.
 * Relative luminance is the relative brightness of any point in a colorspace,
 * normalized to 0 for darkest black and 1 for lightest white.
 * Formula: L = 0.2126 * R + 0.7152 * G + 0.0722 * B
 * Source: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
var calculateRelativeLuminance = function (color) {
    var _a = (0, exports.hexToRGB)(color), r = _a[0], g = _a[1], b = _a[2];
    return (0.2126 * calculateRelativeLuminanceComponent(r) +
        0.7152 * calculateRelativeLuminanceComponent(g) +
        0.0722 * calculateRelativeLuminanceComponent(b));
};
/**
 * Calculates the contrast ratio, or difference in relative luminance, for two
 * overlaid colors.
 * Formula: (L1 + 0.05) / (L2 + 0.05), whereby:
 *   - L1 is the relative luminance of the lighter of the colors, and
 *   - L2 is the relative luminance of the darker of the colors.
 */
var getColorContrastRatio = function (foregroundColor, backgroundColor) {
    var foregroundLuminance = calculateRelativeLuminance(foregroundColor);
    var backgroundLuminance = calculateRelativeLuminance(backgroundColor);
    var l1 = foregroundLuminance >= backgroundLuminance
        ? foregroundLuminance
        : backgroundLuminance;
    var l2 = foregroundLuminance > backgroundLuminance
        ? backgroundLuminance
        : foregroundLuminance;
    var ratio = (l1 + 0.05) / (l2 + 0.05);
    return Math.round(ratio * 100) / 100;
};
exports.getColorContrastRatio = getColorContrastRatio;
/**
 * Checks if the contrast ratio between two colors passes
 * WCAG 2.0 level AA requirements.
 */
var getIsAACompliant = function (foregroundColor, backgroundColor) {
    return (0, exports.getColorContrastRatio)(foregroundColor, backgroundColor) >= 4.5;
};
exports.getIsAACompliant = getIsAACompliant;
/**
 * Checks if the contrast ratio between two colors passes
 * WCAG 2.0 level AAA requirements.
 */
var getIsAAACompliant = function (foregroundColor, backgroundColor) {
    return (0, exports.getColorContrastRatio)(foregroundColor, backgroundColor) >= 7.0;
};
exports.getIsAAACompliant = getIsAAACompliant;
/**
 * Returns a color based on the theme's primary color. If the primary color
 * is too dark, it will return white. Otherwise, black.
 */
var useSecondaryThemeColor = function () {
    var _a;
    var theme = (0, react_1.useTheme)();
    return (0, react_2.useMemo)(function () {
        var _a;
        var primaryColor = ((_a = theme === null || theme === void 0 ? void 0 : theme.colors) === null || _a === void 0 ? void 0 : _a.primary) || theme_1.theme.colors.lightGold;
        var isPalletThemed = primaryColor === theme_1.theme.colors.lightGold;
        if (isPalletThemed)
            return theme_1.theme.colors.gold900;
        return (0, exports.getColorContrastRatio)(theme_1.theme.colors.white, primaryColor) > 2.5
            ? theme_1.theme.colors.white
            : theme_1.theme.colors.gray900;
    }, [(_a = theme === null || theme === void 0 ? void 0 : theme.colors) === null || _a === void 0 ? void 0 : _a.primary]);
};
exports.useSecondaryThemeColor = useSecondaryThemeColor;
